import { useState } from 'react'
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card'
import { Label } from '@/components/ui/label'
import { Button } from '@/components/ui/button'
import { Switch } from '@/components/ui/switch'
import { Tabs, TabsList, TabsTrigger, TabsContent } from '@/components/ui/tabs'
import {
  Bell,
  Database,
  Info,
  FileText,
  Loader2,
  ExternalLink,
  Send,
  Download,
  Mail,
  MessageSquare,
  Link2,
  Settings2,
  CalendarClock,
  Wrench,
} from 'lucide-react'
import { Input } from '@/components/ui/input'
import { toDisplayDate } from '@/lib/utils'
import { useQuery, useQueryClient } from '@tanstack/react-query'
import { useToast } from '@/hooks/use-toast'
import { useVersion } from '@/hooks/use-version'
import { useUser } from '@/contexts/UserContext'
import { GoogleIntegration } from '@/components/GoogleIntegration'
import { MailchimpIntegration } from '@/components/MailchimpIntegration'
import { GoogleChatIntegration } from '@/components/GoogleChatIntegration'
import { apiClient, api } from '@/lib/api'
import PicklistManagement from './PicklistManagement'
import { RenewalTaskSettings } from '@/components/RenewalTaskSettings'

export default function Settings() {
  const { toast } = useToast()
  const { isSuperAdmin, isAdmin, isMarketing } = useUser()
  const { data: versionInfo, isLoading: versionLoading } = useVersion()
  const [emailNotifications, setEmailNotifications] = useState(true)
  const [pushNotifications, setPushNotifications] = useState(true)
  const [sendingReport, setSendingReport] = useState(false)
  const [exporting, setExporting] = useState(false)
  const [exportingIds, setExportingIds] = useState(false)

  const handleSendDailyReport = async () => {
    setSendingReport(true)
    try {
      const result = await apiClient.sendDailyReport()
      if (result.success) {
        toast({
          title: 'Report sent',
          description: `Daily report sent to ${result.recipients} admin${result.recipients === 1 ? '' : 's'}.`,
        })
      } else {
        toast({
          title: 'Report not sent',
          description: result.reason || 'No recipients configured',
          variant: 'destructive',
        })
      }
    } catch (error: any) {
      toast({
        title: 'Failed to send report',
        description: error?.response?.data?.error || error?.message || 'An error occurred',
        variant: 'destructive',
      })
    } finally {
      setSendingReport(false)
    }
  }

  const handlePreviewReport = () => {
    window.open(apiClient.getDailyReportPreviewUrl(), '_blank')
  }

  const handleExportAllRecords = async () => {
    setExporting(true)
    try {
      // Server-side admin-only export. The endpoint enforces requireSuperAdmin —
      // the bundle no longer carries a client-side bulk export to keep this
      // single-point-of-enforcement clean.
      const response = await api.get('/export/all', { responseType: 'blob' })
      const url = URL.createObjectURL(response.data)
      const a = document.createElement('a')
      a.href = url
      a.download = `grm-export-${new Date().toISOString().slice(0, 10)}.zip`
      document.body.appendChild(a)
      a.click()
      a.remove()
      URL.revokeObjectURL(url)
      toast({
        title: 'Export Complete',
        description: 'All CRM records have been exported to a ZIP file.',
      })
    } catch (error: any) {
      const status = error?.response?.status
      const msg =
        status === 403
          ? 'Super Administrator privileges required to export'
          : error?.response?.data?.error || error?.message || 'Failed to export records'
      toast({
        title: 'Export Failed',
        description: msg,
        variant: 'destructive',
      })
    } finally {
      setExporting(false)
    }
  }

  const handleExportRecordIds = async () => {
    setExportingIds(true)
    try {
      const response = await api.get('/export/record-ids', { responseType: 'blob' })
      const url = URL.createObjectURL(response.data)
      const a = document.createElement('a')
      a.href = url
      a.download = `record-ids-${new Date().toISOString().slice(0, 10)}.csv`
      document.body.appendChild(a)
      a.click()
      a.remove()
      URL.revokeObjectURL(url)
      toast({
        title: 'Export Complete',
        description: 'Record IDs downloaded as CSV.',
      })
    } catch (error: any) {
      const status = error?.response?.status
      const msg =
        status === 403
          ? 'Administrator privileges required'
          : error?.response?.data?.error || error?.message || 'Failed to export record IDs'
      toast({
        title: 'Export Failed',
        description: msg,
        variant: 'destructive',
      })
    } finally {
      setExportingIds(false)
    }
  }

  const handleSavePreferences = () => {
    toast({
      title: 'Preferences saved',
      description: 'Your preferences have been updated successfully.',
    })
  }

  return (
    <div className="space-y-6">
      <div>
        <h1 className="text-3xl font-bold tracking-tight">Settings</h1>
        <p className="text-muted-foreground">Manage your account settings and preferences</p>
      </div>

      <Tabs defaultValue="google" className="w-full">
        <TabsList className="w-full flex flex-wrap h-auto gap-1 p-1" aria-label="Settings sections">
          <TabsTrigger value="google" className="flex items-center gap-1.5">
            <Mail className="h-3.5 w-3.5" aria-hidden="true" />
            Google
          </TabsTrigger>
          {(isAdmin || isMarketing) && (
            <TabsTrigger value="mailchimp" className="flex items-center gap-1.5">
              <Link2 className="h-3.5 w-3.5" aria-hidden="true" />
              Mailchimp
            </TabsTrigger>
          )}
          {isAdmin && (
            <TabsTrigger value="google-chat" className="flex items-center gap-1.5">
              <MessageSquare className="h-3.5 w-3.5" aria-hidden="true" />
              Google Chat
            </TabsTrigger>
          )}
          <TabsTrigger value="notifications" className="flex items-center gap-1.5">
            <Bell className="h-3.5 w-3.5" aria-hidden="true" />
            Notifications
          </TabsTrigger>
          {isAdmin && (
            <TabsTrigger value="data" className="flex items-center gap-1.5">
              <Database className="h-3.5 w-3.5" aria-hidden="true" />
              Data & Privacy
            </TabsTrigger>
          )}
          {isAdmin && (
            <TabsTrigger value="reports" className="flex items-center gap-1.5">
              <FileText className="h-3.5 w-3.5" aria-hidden="true" />
              Reports
            </TabsTrigger>
          )}
          {isAdmin && (
            <TabsTrigger value="picklists" className="flex items-center gap-1.5">
              <Settings2 className="h-3.5 w-3.5" aria-hidden="true" />
              Picklists
            </TabsTrigger>
          )}
          {isAdmin && (
            <TabsTrigger value="renewals" className="flex items-center gap-1.5">
              <CalendarClock className="h-3.5 w-3.5" aria-hidden="true" />
              Renewals
            </TabsTrigger>
          )}
          {isAdmin && (
            <TabsTrigger value="system" className="flex items-center gap-1.5">
              <Wrench className="h-3.5 w-3.5" aria-hidden="true" />
              System
            </TabsTrigger>
          )}
          <TabsTrigger value="about" className="flex items-center gap-1.5">
            <Info className="h-3.5 w-3.5" aria-hidden="true" />
            About
          </TabsTrigger>
        </TabsList>

        <TabsContent value="google" className="mt-4">
          <GoogleIntegration />
        </TabsContent>

        {(isAdmin || isMarketing) && (
          <TabsContent value="mailchimp" className="mt-4">
            <MailchimpIntegration />
          </TabsContent>
        )}

        {isAdmin && (
          <TabsContent value="google-chat" className="mt-4">
            <GoogleChatIntegration />
          </TabsContent>
        )}

        <TabsContent value="notifications" className="mt-4">
          <Card>
            <CardHeader>
              <div className="flex items-center gap-2">
                <Bell className="h-5 w-5 text-muted-foreground" aria-hidden="true" />
                <CardTitle>Notifications</CardTitle>
              </div>
              <CardDescription>Configure how you receive notifications</CardDescription>
            </CardHeader>
            <CardContent className="space-y-4">
              <div className="flex items-center justify-between">
                <div className="space-y-0.5">
                  <Label htmlFor="email-notifications">Email Notifications</Label>
                  <p className="text-sm text-muted-foreground">Receive notifications via email</p>
                </div>
                <Switch
                  id="email-notifications"
                  checked={emailNotifications}
                  onCheckedChange={setEmailNotifications}
                  aria-label="Toggle email notifications"
                />
              </div>
              <div className="flex items-center justify-between">
                <div className="space-y-0.5">
                  <Label htmlFor="push-notifications">Push Notifications</Label>
                  <p className="text-sm text-muted-foreground">
                    Receive push notifications in browser
                  </p>
                </div>
                <Switch
                  id="push-notifications"
                  checked={pushNotifications}
                  onCheckedChange={setPushNotifications}
                  aria-label="Toggle push notifications"
                />
              </div>
              <Button onClick={handleSavePreferences}>Save Preferences</Button>
            </CardContent>
          </Card>
        </TabsContent>

        {isAdmin && (
          <TabsContent value="data" className="mt-4">
            <Card>
              <CardHeader>
                <div className="flex items-center gap-2">
                  <Database className="h-5 w-5 text-muted-foreground" aria-hidden="true" />
                  <CardTitle>Data & Privacy</CardTitle>
                </div>
                <CardDescription>Export CRM record IDs and ownership data</CardDescription>
              </CardHeader>
              <CardContent className="space-y-4">
                {isSuperAdmin && (
                  <div className="space-y-2">
                    <Button
                      variant="outline"
                      onClick={handleExportAllRecords}
                      disabled={exporting}
                      aria-label="Export all CRM records to ZIP file"
                    >
                      {exporting ? (
                        <>
                          <Loader2 className="mr-2 h-4 w-4 animate-spin" aria-hidden="true" />
                          Exporting...
                        </>
                      ) : (
                        <>
                          <Download className="mr-2 h-4 w-4" aria-hidden="true" />
                          Export All Records
                        </>
                      )}
                    </Button>
                    <p className="text-sm text-muted-foreground">
                      Download all CRM data as a ZIP file containing CSV exports
                    </p>
                  </div>
                )}

                <div className={isSuperAdmin ? 'space-y-2 border-t pt-4' : 'space-y-2'}>
                  <Button
                    variant="outline"
                    onClick={handleExportRecordIds}
                    disabled={exportingIds}
                    aria-label="Download Record IDs and Owner IDs as CSV"
                  >
                    {exportingIds ? (
                      <>
                        <Loader2 className="mr-2 h-4 w-4 animate-spin" aria-hidden="true" />
                        Exporting...
                      </>
                    ) : (
                      <>
                        <Download className="mr-2 h-4 w-4" aria-hidden="true" />
                        Download Record IDs
                      </>
                    )}
                  </Button>
                  <p className="text-sm text-muted-foreground">
                    CSV of Record ID and Owner ID for all Accounts, Opportunities, Leads, and
                    Contacts — useful for data audits and external system integration.
                  </p>
                </div>
              </CardContent>
            </Card>
          </TabsContent>
        )}

        {isAdmin && (
          <TabsContent value="reports" className="mt-4">
            <Card>
              <CardHeader>
                <div className="flex items-center gap-2">
                  <FileText className="h-5 w-5 text-muted-foreground" aria-hidden="true" />
                  <CardTitle>Reports</CardTitle>
                </div>
                <CardDescription>Daily executive summary reports</CardDescription>
              </CardHeader>
              <CardContent className="space-y-4">
                <div className="space-y-2">
                  <Label>Daily Report</Label>
                  <p className="text-sm text-muted-foreground">
                    Send the daily executive summary to all admin users
                  </p>
                  <div className="flex gap-2 pt-2">
                    <Button
                      variant="outline"
                      onClick={handlePreviewReport}
                      aria-label="Preview daily report in new tab"
                    >
                      <ExternalLink className="mr-2 h-4 w-4" aria-hidden="true" />
                      Preview Report
                    </Button>
                    <Button
                      onClick={handleSendDailyReport}
                      disabled={sendingReport}
                      aria-label="Send daily report to all admin users"
                    >
                      {sendingReport ? (
                        <>
                          <Loader2 className="mr-2 h-4 w-4 animate-spin" aria-hidden="true" />
                          Sending...
                        </>
                      ) : (
                        <>
                          <Send className="mr-2 h-4 w-4" aria-hidden="true" />
                          Send Report Now
                        </>
                      )}
                    </Button>
                  </div>
                </div>
              </CardContent>
            </Card>
          </TabsContent>
        )}

        {isAdmin && (
          <TabsContent value="picklists" className="mt-4">
            <PicklistManagement />
          </TabsContent>
        )}

        {isAdmin && (
          <TabsContent value="renewals" className="mt-4">
            <RenewalTaskSettings />
          </TabsContent>
        )}

        {isAdmin && (
          <TabsContent value="system" className="mt-4">
            <MaintenanceSettings />
          </TabsContent>
        )}

        <TabsContent value="about" className="mt-4">
          <Card>
            <CardHeader>
              <div className="flex items-center gap-2">
                <Info className="h-5 w-5 text-muted-foreground" aria-hidden="true" />
                <CardTitle>About</CardTitle>
              </div>
              <CardDescription>Application version and build information</CardDescription>
            </CardHeader>
            <CardContent>
              {versionLoading ? (
                <p className="text-sm text-muted-foreground">Loading version info...</p>
              ) : versionInfo ? (
                <dl className="grid gap-3 text-sm" aria-label="Version information">
                  <div className="flex justify-between">
                    <dt className="text-muted-foreground">Version</dt>
                    <dd className="font-medium">{versionInfo.version}</dd>
                  </div>
                  <div className="flex justify-between">
                    <dt className="text-muted-foreground">Build Date</dt>
                    <dd className="font-medium">
                      {(
                        toDisplayDate(versionInfo.buildDate) ?? new Date(versionInfo.buildDate)
                      ).toLocaleDateString('en-US', {
                        year: 'numeric',
                        month: 'long',
                        day: 'numeric',
                        hour: '2-digit',
                        minute: '2-digit',
                      })}
                    </dd>
                  </div>
                  <div className="flex justify-between">
                    <dt className="text-muted-foreground">Commit</dt>
                    <dd className="font-mono text-xs">{versionInfo.commitSha}</dd>
                  </div>
                  <div className="flex justify-between">
                    <dt className="text-muted-foreground">Environment</dt>
                    {/* envName, not environment: the latter is NODE_ENV and reads
                        "production" on dev, so this row was stating the opposite of the
                        truth on the one environment where somebody would check it. */}
                    <dd className="font-medium capitalize">
                      {versionInfo.envName ?? versionInfo.environment}
                    </dd>
                  </div>
                </dl>
              ) : (
                <p className="text-sm text-muted-foreground">Version information unavailable</p>
              )}
            </CardContent>
          </Card>
        </TabsContent>
      </Tabs>
    </div>
  )
}

function MaintenanceSettings() {
  const { toast } = useToast()
  const queryClient = useQueryClient()
  const [saving, setSaving] = useState(false)
  const [confirmOpen, setConfirmOpen] = useState(false)

  const { data: maintenance, isLoading } = useQuery({
    queryKey: ['admin', 'maintenance'],
    queryFn: async () => {
      const response = await (apiClient as any).client.get('/admin/maintenance')
      return response.data?.data as { enabled: boolean; message: string }
    },
  })

  const [message, setMessage] = useState('')

  // Sync message input with fetched data
  useState(() => {
    if (maintenance?.message) setMessage(maintenance.message)
  })

  const handleToggle = async (enabled: boolean) => {
    if (enabled) {
      setConfirmOpen(true)
      return
    }
    await saveMaintenance(false)
  }

  const saveMaintenance = async (enabled: boolean) => {
    setSaving(true)
    try {
      await (apiClient as any).client.put('/admin/maintenance', {
        enabled,
        message: message || 'System is under maintenance. Please try again later.',
      })
      queryClient.invalidateQueries({ queryKey: ['admin', 'maintenance'] })
      toast({
        title: enabled ? 'Maintenance Mode Enabled' : 'Maintenance Mode Disabled',
        description: enabled
          ? 'All non-admin users are now locked out.'
          : 'All users can now access the system.',
      })
      setConfirmOpen(false)
    } catch (error) {
      toast({
        title: 'Error',
        description: 'Failed to update maintenance mode.',
        variant: 'destructive',
      })
    } finally {
      setSaving(false)
    }
  }

  if (isLoading) return <div className="py-8 text-center text-muted-foreground">Loading...</div>

  return (
    <>
      <Card>
        <CardHeader>
          <div className="flex items-center gap-2">
            <Wrench className="h-5 w-5 text-muted-foreground" aria-hidden="true" />
            <CardTitle>System Maintenance</CardTitle>
          </div>
          <CardDescription>
            Suspend the application for maintenance. Non-admin users will be unable to access the
            CRM until maintenance mode is turned off.
          </CardDescription>
        </CardHeader>
        <CardContent className="space-y-4">
          <div className="flex items-center justify-between">
            <div>
              <Label htmlFor="maintenance-toggle" className="text-base font-medium">
                Maintenance Mode
              </Label>
              <p className="text-sm text-muted-foreground mt-0.5">
                {maintenance?.enabled
                  ? 'System is currently suspended'
                  : 'System is running normally'}
              </p>
            </div>
            <Switch
              id="maintenance-toggle"
              checked={maintenance?.enabled ?? false}
              onCheckedChange={handleToggle}
              disabled={saving}
              aria-label="Toggle maintenance mode"
            />
          </div>
          <div>
            <Label htmlFor="maintenance-message">Maintenance Message</Label>
            <Input
              id="maintenance-message"
              value={message || maintenance?.message || ''}
              onChange={(e) => setMessage(e.target.value)}
              placeholder="System is under maintenance. Please try again later."
              aria-label="Custom maintenance message shown to users"
              className="mt-1"
            />
            <p className="text-xs text-muted-foreground mt-1">
              This message is displayed to users when maintenance mode is active.
            </p>
          </div>
        </CardContent>
      </Card>

      {/* Confirmation dialog */}
      {confirmOpen && (
        <div
          className="fixed inset-0 z-50 flex items-center justify-center bg-black/50"
          role="dialog"
          aria-modal="true"
          aria-label="Confirm maintenance mode"
        >
          <div className="bg-background rounded-lg p-6 max-w-md shadow-lg">
            <h2 className="text-lg font-semibold mb-2">Enable Maintenance Mode?</h2>
            <p className="text-muted-foreground mb-4">
              This will immediately lock out all non-admin users from the CRM. They will see a
              maintenance page until you turn it off.
            </p>
            <div className="flex justify-end gap-3">
              <Button variant="outline" onClick={() => setConfirmOpen(false)} disabled={saving}>
                Cancel
              </Button>
              <Button variant="destructive" onClick={() => saveMaintenance(true)} disabled={saving}>
                {saving ? 'Enabling...' : 'Enable Maintenance Mode'}
              </Button>
            </div>
          </div>
        </div>
      )}
    </>
  )
}
